<!-- ── Hero ── -->
<section class="hero">
  <div class="hero-inner">
    <div class="hero-left">
      <h1>Timesheet</h1>
      <p class="hero-sub">{{ readOnly ? 'View time entries by project' : 'Record and manage your time entries by project' }}</p>
    </div>
    <div class="hero-right">
      <div class="hero-kpi" *ngIf="recentEntries.length > 0">
        <mat-icon>schedule</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ calculateRecentTotal() }}</span>
          <span class="kpi-label">Hours</span>
        </div>
      </div>
      <div class="hero-kpi accent" *ngIf="recentEntries.length > 0">
        <mat-icon>list_alt</mat-icon>
        <div class="kpi-text">
          <span class="kpi-value">{{ recentEntries.length }}</span>
          <span class="kpi-label">Entries</span>
        </div>
      </div>
    </div>
  </div>
</section>

<div class="timesheet-shell">

  <!-- Status bars -->
  <div *ngIf="success" class="status-bar success">
    <mat-icon>check_circle</mat-icon> {{ success }}
  </div>
  <div *ngIf="error" class="status-bar error">
    <mat-icon>error</mat-icon> {{ error }}
  </div>
  <div *ngIf="loading" class="status-bar info">
    <mat-icon class="spinner">hourglass_empty</mat-icon> Processing…
  </div>

  <!-- Project Selector Card -->
  <div class="card selector-card">
    <div class="card-header">
      <mat-icon>folder_open</mat-icon>
      <h3>Select Project</h3>
    </div>
    <div class="card-body selector-body">
      <div class="select-wrap">
        <mat-icon class="select-icon">business</mat-icon>
        <select
          id="project-select"
          [(ngModel)]="selectedProject"
          (ngModelChange)="onProjectSelected()">
          <option [ngValue]="null" disabled>Choose a project…</option>
          <option *ngFor="let project of projects" [ngValue]="project.projectNumber">
            {{ project.projectNumber }} — {{ project.projectName }}
          </option>
        </select>
      </div>
      <div *ngIf="projectsLoading" class="inline-loading">
        <mat-icon class="spinner">hourglass_empty</mat-icon> Loading projects…
      </div>
    </div>
  </div>

  <!-- Time Entry Form Card -->
  <div *ngIf="!readOnly" class="card entry-card">
    <div class="card-header">
      <mat-icon>edit_note</mat-icon>
      <h3>Time Entry Details</h3>
    </div>
    <div class="card-body">
      <div class="form-grid">
        <!-- Work Code -->
        <div class="field span-half">
          <label for="workCode">Work Code</label>
          <p class="field-hint">Select the type of work performed</p>
          <div class="input-wrap select-wrap">
            <mat-icon class="input-icon">code</mat-icon>
            <select
              id="workCode"
              [(ngModel)]="selectedWorkCode">
              <option value="">-- Select work code --</option>
              <option *ngFor="let code of workCodes" [value]="code.code">
                {{ code.description }}
              </option>
            </select>
          </div>
        </div>

        <!-- Job Type -->
        <div class="field span-half">
          <label for="jobType">Job Type</label>
          <p class="field-hint">Select the EWO this time is for</p>
          <div class="input-wrap select-wrap">
            <mat-icon class="input-icon">work</mat-icon>
            <select
              id="jobType"
              [(ngModel)]="selectedJobType"
              [disabled]="!selectedProject || jobTypesLoading">
              <option value="">-- Select job type --</option>
              <option *ngFor="let type of jobTypes" [value]="type.code">
                {{ type.description }}
              </option>
            </select>
          </div>
          <div *ngIf="jobTypesLoading" class="inline-loading">
            <mat-icon class="spinner">hourglass_empty</mat-icon> Loading job types…
          </div>
        </div>

        <!-- Hours -->
        <div class="field span-full">
          <label for="hours">Hours</label>
          <p class="field-hint">Number of hours to log (use 0 to delete an existing entry)</p>
          <div class="input-wrap">
            <mat-icon class="input-icon">timer</mat-icon>
            <input
              type="number"
              id="hours"
              [(ngModel)]="hours"
              min="0"
              step="0.5"
              placeholder="0.0"
            />
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button
          class="btn-primary"
          (click)="submitTimesheetEntry()"
          [disabled]="!selectedProject || !selectedWorkCode || !selectedJobType || hours < 0 || loading">
          <mat-icon *ngIf="!loading">send</mat-icon>
          <mat-icon *ngIf="loading" class="spinner">hourglass_empty</mat-icon>
          {{ loading ? 'Submitting…' : 'Submit Time Entry' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Recent Entries Card -->
  <div class="card recent-card" *ngIf="selectedProject">
    <div class="card-header">
      <mat-icon>history</mat-icon>
      <h3>Recent Entries</h3>
    </div>

    <div class="empty-state" *ngIf="recentEntries.length === 0">
      <mat-icon>inbox</mat-icon>
      <p>No recent entries for this project</p>
    </div>

    <div *ngIf="recentEntries.length > 0">
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Work Code</th>
              <th>Job Type</th>
              <th>Employee</th>
              <th>Hours</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let e of recentEntries">
              <td class="cell-date">{{ e.date | date: 'MMM dd, yyyy' }}</td>
              <td>{{ getWorkCodeDescription(e.workCode) }}</td>
              <td>{{ e.jobType || '—' }}</td>
              <td>{{ getUsernameById(e.employee) }}</td>
              <td class="cell-hours">{{ e.hours }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="table-footer">
        <span class="footer-label">{{ recentEntries.length }} entr{{ recentEntries.length !== 1 ? 'ies' : 'y' }}</span>
        <span class="footer-total">Total: <strong>{{ calculateRecentTotal() }} hrs</strong></span>
      </div>
    </div>
  </div>

</div>

<!-- Delete confirmation modal -->
<div *ngIf="showDeleteModal" class="modal-backdrop">
  <div class="modal" role="dialog" aria-modal="true" aria-labelledby="deleteModalTitle">
    <div class="modal-header">
      <mat-icon class="modal-icon">warning</mat-icon>
      <h4 id="deleteModalTitle">Confirm Delete</h4>
    </div>
    <p>Are you sure you want to delete the entry for <strong>{{ pendingDeleteEntry?.date | date:'MMM dd, yyyy' }}</strong> (Work Code: <strong>{{ pendingDeleteEntry?.workCode }}</strong>)?</p>
    <div class="modal-actions">
      <button class="btn-cancel" (click)="cancelDelete()" [disabled]="loading">Cancel</button>
      <button class="btn-danger" (click)="confirmDelete()" [disabled]="loading">
        <mat-icon *ngIf="!loading">delete</mat-icon>
        <mat-icon *ngIf="loading" class="spinner">hourglass_empty</mat-icon>
        {{ loading ? 'Deleting…' : 'Yes, Delete' }}
      </button>
    </div>
  </div>
</div>
